/**
 * SeanceLive.jsx — Carnet de briefing + Guidage mobile pour l'enseignant.
 *
 * Route : /enseignant/seance-live/:code
 *
 * 3 vues séquentielles (tab) :
 *  1. BRIEFING  — style "feuille de cahier", à utiliser debout à côté de la moto
 *                 avant que l'élève monte. Pré-rempli depuis le livret REMC +
 *                 objectifsConcis. Le moniteur peut ajouter des notes manuelles.
 *  2. GUIDAGE   — mobile-first, gros boutons. Pendant la phase rouler :
 *                 cocher OK / À retravailler par critère + script radio
 *                 (FORT / MOYEN / FAIBLE) + compteur de passages + notes.
 *  3. DÉBRIEF   — récap : % critères OK, notes briefing + guidage, partage.
 *
 * Persistance : `useSeanceLive(code)` → localStorage `c2r:seance-live:v1`.
 */
import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, BookOpen, Target, ListChecks,
  PenLine, Quote, Radio, Plus, Minus, CheckCircle2, XCircle,
  Circle, BarChart3, RotateCcw, Save, ChevronDown, ChevronUp,
  FileDown, Share2, Copy, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import useSeo from '../hooks/useSeo';
import useSeanceLive from '../hooks/useSeanceLive';
import { getObjectifConcis } from '../data/teacher/objectifsConcis';
import { getLivretItem, FICHE_BY_CODE } from '../data/teacher/evalInference';
import { getScriptRadio } from '../data/teacher/scriptsRadio';
import { getBriefingSchema } from '../data/teacher/briefingSchemas';
import { exportSeancePdf } from '../utils/exportSeancePdf';
import { buildShareUrl } from '../utils/seanceShare';

const CATEGORIE_COLORS = {
  C1: '#0891b2',
  C2: '#7c3aed',
  C3: '#dc2626',
  C4: '#16a34a',
};

const TABS = [
  { id: 'briefing', label: 'Briefing',  icon: BookOpen, sub: 'À côté de la moto' },
  { id: 'guidage',  label: 'Guidage',   icon: Radio,    sub: 'Pendant la circulation' },
  { id: 'debrief',  label: 'Débrief',   icon: BarChart3, sub: 'Récap & partage' },
];

/* ─── Vue BRIEFING ───────────────────────────────────────────────── */
function BriefingView({ code, livret, concis, phraseClef, state, updateField, accent, onNext }) {
  // Couleurs forcées pour préserver l'aspect "cahier crème" même en dark mode
  const inkPrimary   = '#1c1917'; // stone-900
  const inkSecondary = '#44403c'; // stone-700
  const inkMuted     = '#78716c'; // stone-500
  const Schema = getBriefingSchema(code);
  return (
    <div
      className="rounded-3xl p-6 sm:p-8 shadow-lg"
      style={{
        backgroundColor: '#fefcf6',
        backgroundImage:
          'repeating-linear-gradient(transparent 0px, transparent 31px, rgba(0,0,0,0.04) 31px, rgba(0,0,0,0.04) 32px), linear-gradient(180deg, #fefcf6 0%, #fdf9ee 100%)',
        border: `2px solid ${accent}33`,
        color: inkPrimary,
        colorScheme: 'light',
      }}
      data-testid="briefing-view"
    >
      {/* Marge rouge type cahier */}
      <div className="relative pl-4 sm:pl-6" style={{ borderLeft: `3px solid ${accent}55` }}>
        {/* En-tête */}
        <div className="mb-5">
          <div
            className="inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-white mb-2"
            style={{ background: accent }}
          >
            REMC · {code}
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: inkPrimary }}
            data-testid="briefing-intitule"
          >
            {livret?.intitule || `Compétence ${code}`}
          </h2>
        </div>

        {/* Objectif */}
        <section className="mb-6">
          <div
            className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-1.5"
            style={{ color: accent }}
          >
            <Target className="w-3.5 h-3.5" /> Objectif de la séance
          </div>
          <p
            className="text-xl sm:text-2xl font-black leading-snug italic"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: inkPrimary }}
            data-testid="briefing-objectif"
          >
            « {concis?.objectifCourt || livret?.objectif || 'Objectif à définir'} »
          </p>
        </section>

        {/* Mini-schéma contextuel */}
        {Schema && (
          <section className="mb-6" data-testid="briefing-schema">
            <div
              className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2"
              style={{ color: accent }}
            >
              <BookOpen className="w-3.5 h-3.5" /> Repère visuel
            </div>
            <div
              className="rounded-2xl overflow-hidden p-3"
              style={{
                background: `${accent}0a`,
                border: `1.5px solid ${accent}33`,
                color: inkPrimary,
              }}
            >
              <div className="aspect-[200/120] w-full" style={{ color: inkSecondary }}>
                <Schema accent={accent} />
              </div>
            </div>
          </section>
        )}

        {/* Critères */}
        {(concis?.criteresPrecis || []).length > 0 && (
          <section className="mb-6">
            <div
              className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2"
              style={{ color: accent }}
            >
              <ListChecks className="w-3.5 h-3.5" /> Critères à valider
            </div>
            <ul className="space-y-2">
              {concis.criteresPrecis.map((c, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className="inline-flex w-6 h-6 rounded-md border-2 items-center justify-center text-xs font-black mt-0.5 flex-shrink-0"
                    style={{ borderColor: `${accent}88`, color: accent, background: '#fff' }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-base leading-snug pt-0.5" style={{ color: inkPrimary }}>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Phrase clé */}
        {phraseClef && (
          <section className="mb-6">
            <div
              className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-1.5"
              style={{ color: accent }}
            >
              <Quote className="w-3.5 h-3.5" /> Phrase à lui dire
            </div>
            <p
              className="text-lg italic font-bold pl-3"
              style={{ borderLeft: `4px solid ${accent}`, color: inkSecondary }}
            >
              « {phraseClef} »
            </p>
          </section>
        )}

        {/* Notes briefing */}
        <section className="mb-6">
          <div
            className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-1.5"
            style={{ color: accent }}
          >
            <PenLine className="w-3.5 h-3.5" /> Mes notes briefing
          </div>
          <textarea
            value={state.briefingNotes}
            onChange={(e) => updateField('briefingNotes', e.target.value)}
            placeholder="Ce que je vais ajouter à l'oral, contexte de l'élève, points d'attention..."
            rows={4}
            data-testid="briefing-notes"
            className="w-full p-3 rounded-xl border-2 text-sm leading-relaxed focus:outline-none transition-colors"
            style={{
              borderColor: `${accent}33`,
              background: '#ffffff',
              color: inkPrimary,
            }}
            onFocus={(e) => { e.target.style.borderColor = accent; }}
            onBlur={(e) => { e.target.style.borderColor = `${accent}33`; }}
          />
        </section>

        {/* CTA */}
        <div className="flex justify-end pt-2">
          <Button
            onClick={onNext}
            className="gap-2 text-white font-black uppercase tracking-wider px-6 py-5 text-sm"
            style={{ background: accent }}
            data-testid="briefing-next"
          >
            Maintenant on roule <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {/* Hint placeholder couleur muted */}
      <div className="sr-only" style={{ color: inkMuted }}>placeholder-color-marker</div>
    </div>
  );
}

/* ─── Vue GUIDAGE ────────────────────────────────────────────────── */
function CritereRow({ index, label, statut, onChange, accent }) {
  return (
    <li
      className="rounded-xl border-2 p-3 bg-card transition-colors"
      style={{ borderColor: statut === 'ok' ? '#16a34a' : statut === 'ko' ? '#dc2626' : `${accent}33` }}
      data-testid={`critere-row-${index}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <span
          className="inline-flex w-6 h-6 rounded-md items-center justify-center text-white text-xs font-black mt-0.5 flex-shrink-0"
          style={{ background: accent }}
        >
          {index + 1}
        </span>
        <span className="text-sm font-bold leading-snug pt-0.5 flex-1">{label}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {[
          { id: 'ok', label: 'OK', color: '#16a34a', Icon: CheckCircle2 },
          { id: 'ko', label: 'À retravailler', color: '#dc2626', Icon: XCircle },
          { id: null, label: 'Pas testé', color: '#64748b', Icon: Circle },
        ].map((opt) => {
          const active = statut === opt.id || (statut == null && opt.id === null);
          const Icon = opt.Icon;
          return (
            <button
              key={String(opt.id)}
              type="button"
              onClick={() => onChange(opt.id)}
              data-testid={`critere-${index}-${opt.id || 'reset'}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors border-2"
              style={
                active
                  ? { background: opt.color, color: '#fff', borderColor: opt.color }
                  : { background: 'transparent', color: opt.color, borderColor: `${opt.color}55` }
              }
              aria-pressed={active}
            >
              <Icon className="w-3 h-3" />
              {opt.label}
            </button>
          );
        })}
      </div>
    </li>
  );
}

function GuidageView({
  code, livret, concis, scripts, state, stats,
  updateField, setCritereStatut, incrementPassages, accent, onNext,
}) {
  const [niveau, setNiveau] = useState('fort');
  const phrases = scripts[niveau] || [];

  return (
    <div className="space-y-4" data-testid="guidage-view">
      {/* Sticky header */}
      <div
        className="sticky top-2 z-10 rounded-2xl p-3 shadow-lg backdrop-blur"
        style={{ background: 'rgba(0,0,0,0.85)', border: `1px solid ${accent}55` }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-white"
              style={{ background: accent }}
            >
              REMC · {code}
            </div>
            <p className="text-xs sm:text-sm font-black text-white leading-tight mt-1 line-clamp-2">
              {concis?.objectifCourt || livret?.intitule}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] text-white/60 uppercase tracking-widest font-black">Critères</div>
            <div className="text-lg font-black text-white tabular-nums">
              {stats.okCount}<span className="text-white/40">/{(concis?.criteresPrecis || []).length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Compteur passages */}
      <div
        className="rounded-2xl p-4 flex items-center justify-between gap-3"
        style={{ background: `${accent}11`, border: `2px solid ${accent}33` }}
      >
        <div>
          <div
            className="text-[10px] font-black uppercase tracking-widest mb-0.5"
            style={{ color: accent }}
          >
            Passages
          </div>
          <div className="text-3xl font-black tabular-nums" style={{ color: accent, fontFamily: "'Source Code Pro', monospace" }}>
            {state.passages}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => incrementPassages(-1)}
            disabled={state.passages === 0}
            data-testid="passage-minus"
            className="w-12 h-12 rounded-xl border-2 flex items-center justify-center transition-colors disabled:opacity-30"
            style={{ borderColor: `${accent}55`, color: accent }}
            aria-label="Retirer un passage"
          >
            <Minus className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => incrementPassages(1)}
            data-testid="passage-plus"
            className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors text-white"
            style={{ background: accent }}
            aria-label="Ajouter un passage"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Critères tactiles */}
      {(concis?.criteresPrecis || []).length > 0 && (
        <section>
          <h3
            className="text-[11px] font-black uppercase tracking-widest mb-2 px-1 flex items-center gap-1"
            style={{ color: accent }}
          >
            <ListChecks className="w-3.5 h-3.5" /> Critères en temps réel
          </h3>
          <ul className="space-y-2">
            {concis.criteresPrecis.map((c, i) => (
              <CritereRow
                key={i}
                index={i}
                label={c}
                statut={state.critereStatuts[i] || null}
                onChange={(st) => setCritereStatut(i, st)}
                accent={accent}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Script radio */}
      <section>
        <h3
          className="text-[11px] font-black uppercase tracking-widest mb-2 px-1 flex items-center gap-1"
          style={{ color: accent }}
        >
          <Radio className="w-3.5 h-3.5" /> Script radio (à dire à voix haute)
        </h3>
        <div className="flex gap-1 mb-2">
          {[
            { id: 'fort',   label: 'FORT',   sub: 'Verbalisation complète' },
            { id: 'moyen',  label: 'MOYEN',  sub: 'Mots-clés' },
            { id: 'faible', label: 'FAIBLE', sub: 'Silence / signal' },
          ].map((n) => {
            const active = niveau === n.id;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => setNiveau(n.id)}
                data-testid={`radio-tab-${n.id}`}
                className="flex-1 px-2 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-colors border-2"
                style={
                  active
                    ? { background: accent, color: '#fff', borderColor: accent }
                    : { background: 'transparent', color: accent, borderColor: `${accent}44` }
                }
                aria-pressed={active}
              >
                {n.label}
                <span className="block text-[8px] font-bold normal-case opacity-80 mt-0.5">{n.sub}</span>
              </button>
            );
          })}
        </div>
        <div
          className="rounded-2xl p-3 space-y-1.5"
          style={{ background: `${accent}0d`, border: `2px solid ${accent}22` }}
        >
          {phrases.map((p, i) => (
            <p
              key={i}
              className="text-base font-black leading-snug text-foreground"
              data-testid={`radio-phrase-${niveau}-${i}`}
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Notes guidage */}
      <section>
        <h3
          className="text-[11px] font-black uppercase tracking-widest mb-2 px-1 flex items-center gap-1"
          style={{ color: accent }}
        >
          <PenLine className="w-3.5 h-3.5" /> Notes guidage
        </h3>
        <textarea
          value={state.guidageNotes}
          onChange={(e) => updateField('guidageNotes', e.target.value)}
          placeholder="Observations live, axe précis qui revient, idées débrief..."
          rows={3}
          data-testid="guidage-notes"
          className="w-full p-3 rounded-xl border-2 bg-card text-foreground placeholder:text-muted-foreground text-sm leading-relaxed focus:outline-none transition-colors"
          style={{ borderColor: `${accent}33` }}
          onFocus={(e) => { e.target.style.borderColor = accent; }}
          onBlur={(e) => { e.target.style.borderColor = `${accent}33`; }}
        />
      </section>

      {/* CTA */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={onNext}
          className="gap-2 text-white font-black uppercase tracking-wider px-6 py-5 text-sm"
          style={{ background: accent }}
          data-testid="guidage-next"
        >
          Voir le débrief <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/* ─── Vue DÉBRIEF ─────────────────────────────────────────────────── */
function DebriefView({ code, livret, concis, phraseClef, state, stats, accent, ficheRoute, onReset }) {
  const [showOfficiel, setShowOfficiel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const okList = (concis?.criteresPrecis || []).map((c, i) => ({ c, i })).filter(({ i }) => state.critereStatuts[i] === 'ok');
  const koList = (concis?.criteresPrecis || []).map((c, i) => ({ c, i })).filter(({ i }) => state.critereStatuts[i] === 'ko');

  const shareUrl = useMemo(() => {
    if (!shareOpen) return '';
    return buildShareUrl({ code, state });
  }, [shareOpen, code, state]);

  const handleExportPdf = () => {
    try {
      const filename = exportSeancePdf({
        code,
        livret,
        concis,
        phraseClef,
        state,
        stats,
        accent,
      });
      toast.success('PDF exporté', {
        description: filename,
      });
    } catch (err) {
      console.error('Export PDF échoué', err);
      toast.error('Export PDF impossible', {
        description: 'Une erreur est survenue, réessaie dans un instant.',
      });
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copie impossible', { description: 'Sélectionne et copie le lien manuellement.' });
    }
  };

  const handleNativeShare = async () => {
    if (!shareUrl || !navigator.share) return;
    try {
      await navigator.share({
        title: `Récap séance ${code}`,
        text: `Voici le récap de notre séance moto (${livret?.intitule || code}).`,
        url: shareUrl,
      });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="space-y-4" data-testid="debrief-view">
      {/* Pourcentage central */}
      <div
        className="rounded-3xl p-6 text-center"
        style={{ background: `linear-gradient(135deg, ${accent}22 0%, ${accent}08 100%)`, border: `2px solid ${accent}55` }}
      >
        <div
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-white mb-2"
          style={{ background: accent }}
        >
          REMC · {code}
        </div>
        <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-1">
          Critères validés
        </p>
        <div
          className="text-6xl sm:text-7xl font-black tabular-nums leading-none my-2"
          style={{ color: accent, fontFamily: "'Source Code Pro', monospace" }}
          data-testid="debrief-pct"
        >
          {stats.okRatio != null ? `${stats.okRatio}%` : '—'}
        </div>
        <p className="text-sm text-muted-foreground">
          {stats.okCount} sur {stats.totalTested} critères testés
          {state.passages > 0 && (
            <span className="block mt-1 text-xs">· {state.passages} passages effectués</span>
          )}
        </p>
      </div>

      {/* Critères OK */}
      {okList.length > 0 && (
        <section
          className="rounded-2xl p-4"
          style={{ background: 'rgba(22,163,74,0.08)', border: '2px solid rgba(22,163,74,0.3)' }}
        >
          <h3 className="text-[11px] font-black uppercase tracking-widest mb-2 flex items-center gap-1 text-[#16a34a]">
            <CheckCircle2 className="w-3.5 h-3.5" /> Acquis ({okList.length})
          </h3>
          <ul className="space-y-1" data-testid="debrief-ok-list">
            {okList.map(({ c, i }) => (
              <li key={i} className="text-sm flex items-start gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Critères à retravailler */}
      {koList.length > 0 && (
        <section
          className="rounded-2xl p-4"
          style={{ background: 'rgba(220,38,38,0.08)', border: '2px solid rgba(220,38,38,0.3)' }}
        >
          <h3 className="text-[11px] font-black uppercase tracking-widest mb-2 flex items-center gap-1 text-[#dc2626]">
            <XCircle className="w-3.5 h-3.5" /> À retravailler ({koList.length})
          </h3>
          <ul className="space-y-1" data-testid="debrief-ko-list">
            {koList.map(({ c, i }) => (
              <li key={i} className="text-sm flex items-start gap-1.5">
                <XCircle className="w-4 h-4 text-[#dc2626] flex-shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Notes consolidées */}
      {(state.briefingNotes || state.guidageNotes) && (
        <section className="rounded-2xl p-4 bg-card border-2 border-border">
          <h3 className="text-[11px] font-black uppercase tracking-widest mb-2 flex items-center gap-1" style={{ color: accent }}>
            <PenLine className="w-3.5 h-3.5" /> Notes de séance
          </h3>
          {state.briefingNotes && (
            <div className="mb-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mb-1">Briefing</div>
              <p className="text-sm leading-relaxed whitespace-pre-line">{state.briefingNotes}</p>
            </div>
          )}
          {state.guidageNotes && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mb-1">Guidage</div>
              <p className="text-sm leading-relaxed whitespace-pre-line">{state.guidageNotes}</p>
            </div>
          )}
        </section>
      )}

      {/* Toggle libellé officiel */}
      {livret?.objectif && (
        <section>
          <button
            type="button"
            onClick={() => setShowOfficiel((o) => !o)}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-muted-foreground hover:text-foreground"
            data-testid="toggle-officiel"
          >
            {showOfficiel ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showOfficiel ? 'Masquer' : 'Voir'} le libellé officiel REMC
          </button>
          {showOfficiel && (
            <div className="mt-2 p-3 rounded-xl bg-muted/40 border border-border text-xs italic text-muted-foreground leading-relaxed">
              {livret.objectif}
            </div>
          )}
        </section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          type="button"
          onClick={handleExportPdf}
          className="gap-2 text-white font-black uppercase tracking-wider"
          style={{ background: accent }}
          data-testid="debrief-export-pdf"
        >
          <FileDown className="w-4 h-4" /> Exporter PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShareOpen(true)}
          className="gap-2 font-black uppercase tracking-wider"
          style={{ borderColor: accent, color: accent }}
          data-testid="debrief-share"
        >
          <Share2 className="w-4 h-4" /> Partager
        </Button>
        {ficheRoute && (
          <Button asChild variant="outline" className="gap-2" data-testid="debrief-fiche">
            <Link to={ficheRoute}>
              <BookOpen className="w-4 h-4" /> Fiche cours
            </Link>
          </Button>
        )}
        <Button asChild variant="outline" className="gap-2" data-testid="debrief-livret">
          <Link to={`/livret#${code}`}>
            <ListChecks className="w-4 h-4" /> Livret REMC
          </Link>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onReset}
          className="gap-2 ml-auto text-destructive"
          data-testid="debrief-reset"
        >
          <RotateCcw className="w-4 h-4" /> Réinitialiser
        </Button>
      </div>

      {/* Dialog partage QR */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" style={{ color: accent }} /> Partager la séance
            </DialogTitle>
            <DialogDescription>
              Scanne ce QR avec l&apos;élève ou copie le lien — il ouvrira un récap mobile-friendly de la séance.
            </DialogDescription>
          </DialogHeader>

          {shareUrl && (
            <div className="space-y-4">
              {/* QR code */}
              <div className="flex justify-center p-4 bg-white rounded-2xl">
                <QRCodeSVG
                  value={shareUrl}
                  size={220}
                  level="M"
                  bgColor="#ffffff"
                  fgColor={accent}
                  data-testid="share-qrcode"
                />
              </div>

              {/* Lien (lecture seule + bouton copier) */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border-2 border-border bg-muted text-xs font-mono truncate focus:outline-none"
                  data-testid="share-link-input"
                  aria-label="Lien de partage"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="gap-1.5 flex-shrink-0"
                  data-testid="share-copy"
                  aria-label="Copier le lien"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copié' : 'Copier'}
                </Button>
              </div>

              {/* Partage natif (mobile) */}
              {typeof navigator !== 'undefined' && navigator.share && (
                <Button
                  type="button"
                  onClick={handleNativeShare}
                  className="w-full gap-2 text-white font-black"
                  style={{ background: accent }}
                  data-testid="share-native"
                >
                  <Share2 className="w-4 h-4" /> Partager via…
                </Button>
              )}

              <p className="text-[11px] italic text-muted-foreground text-center">
                🔒 Aucune donnée n&apos;est envoyée à un serveur — le récap est encodé dans le lien lui-même.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hint partage */}
      <p className="text-[11px] italic text-muted-foreground text-center pt-2">
        💾 Tes notes sont sauvegardées automatiquement sur cet appareil.
      </p>
    </div>
  );
}

/* ─── Composant principal ────────────────────────────────────────── */
export default function SeanceLive() {
  const { code } = useParams();
  const navigate = useNavigate();
  const livret = useMemo(() => getLivretItem(code), [code]);
  const concis = useMemo(() => getObjectifConcis(code), [code]);
  const phraseClef = livret?.lecon?.phraseClef || null;
  const ficheInfo = FICHE_BY_CODE[code] || null;
  const ficheRoute = ficheInfo ? `${ficheInfo.hub}/${ficheInfo.slug}` : null;
  const categorie = code ? code.split('-')[0] : 'C1';
  const accent = CATEGORIE_COLORS[categorie] || '#0891b2';
  const scripts = useMemo(() => getScriptRadio(code, concis?.objectifCourt), [code, concis]);

  const seance = useSeanceLive(code);
  const [tab, setTab] = useState('briefing');

  useSeo({
    title: livret ? `Séance live · ${code} ${livret.intitule} — Espace enseignant` : 'Séance live',
    description: 'Carnet de briefing + guidage mobile pour piloter une séance en temps réel.',
    path: `/enseignant/seance-live/${code}`,
  });

  if (!livret) {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-black mb-2">Code REMC introuvable</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Le code « {code} » n&apos;existe pas dans le livret d&apos;apprentissage.
          </p>
          <Button asChild>
            <Link to="/enseignant/eval-plateau">
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l&apos;évaluation
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12" data-testid="seance-live-page">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-muted-foreground hover:text-foreground"
            data-testid="btn-back"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Séance Live
          </span>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className="flex flex-col items-center gap-0.5 p-2.5 rounded-xl border-2 transition-colors"
                style={
                  active
                    ? { background: accent, borderColor: accent, color: '#fff' }
                    : { background: 'transparent', borderColor: `${accent}44`, color: accent }
                }
                aria-pressed={active}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">{t.label}</span>
                <span className="text-[9px] font-bold normal-case opacity-80 hidden sm:block">{t.sub}</span>
              </button>
            );
          })}
        </div>

        {/* Contenu de la vue active */}
        {tab === 'briefing' && (
          <BriefingView
            code={code}
            livret={livret}
            concis={concis}
            phraseClef={phraseClef}
            state={seance.state}
            updateField={seance.updateField}
            accent={accent}
            onNext={() => setTab('guidage')}
          />
        )}
        {tab === 'guidage' && (
          <GuidageView
            code={code}
            livret={livret}
            concis={concis}
            scripts={scripts}
            state={seance.state}
            stats={seance.stats}
            updateField={seance.updateField}
            setCritereStatut={seance.setCritereStatut}
            incrementPassages={seance.incrementPassages}
            accent={accent}
            onNext={() => setTab('debrief')}
          />
        )}
        {tab === 'debrief' && (
          <DebriefView
            code={code}
            livret={livret}
            concis={concis}
            phraseClef={phraseClef}
            state={seance.state}
            stats={seance.stats}
            accent={accent}
            ficheRoute={ficheRoute}
            onReset={() => {
              if (window.confirm('Réinitialiser cette séance ? Toutes les notes et coches seront perdues.')) {
                seance.reset();
                setTab('briefing');
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
